import React, { useState, useEffect, useMemo } from 'react';
import { formatAppMoney } from '../../services/appConfigRuntime';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Pagination,
  Stack,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  CheckCircle,
  Warning,
  Info,
  SyncAlt,
  Search,
  FilterList,
  Refresh,
  Close,
  FileDownload,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + 'synctrack';
const CONTAINER_ASSIGNMENTS_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync-container-assignments/sync-file`;

const ContractToPurchaseDatapoint = () => {
  const { flowInstanceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { masterFlowId, flowName, tenantId: stateTenantId } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [poData, setPoData] = useState(null);
  const [error, setError] = useState(null);
  const [containerId, setContainerId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);

  const tenantId = stateTenantId ?? localStorage.getItem('tenantId') ?? '1';

  useEffect(() => {
    if (flowInstanceId && tenantId) {
      fetchDataPointConfig();
    } else {
      setError('Missing required parameters');
      setLoading(false);
    }
  }, [flowInstanceId, tenantId]);

  const fetchAssignedContainers = async (token, flowId) => {
    try {
      const res = await fetch(`${CONTAINER_ASSIGNMENTS_URL}/${flowId}`, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok && res.status !== 404) return [];
      const data = await (res.status === 404 ? [] : res.json());
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  };

  const fetchDataPointConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const configRes = await fetch(`${API_BASE_URL}/datapoint/flow/${flowInstanceId}/tenant/${tenantId}`, {
        method: 'GET',
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!configRes.ok) throw new Error(`Failed to fetch config: ${configRes.status}`);
      const configData = await configRes.json();
      setConfig(configData);

      const flowIdForConnector = configData.flowInstanceId || configData.masterFlowId;
      if (!flowIdForConnector) {
        setLoading(false);
        return;
      }

      const assignments = await fetchAssignedContainers(token, flowIdForConnector);
      const firstContainerId = assignments.length > 0 ? assignments[0].conId : null;
      setContainerId(firstContainerId);

      // Prefer connector type (stable); fall back to mcName when mc_connector_type is null (e.g. seed data)
      const autodeskConnector = configData.connectors?.find((c) =>
        (c.mcConnectorType && c.mcConnectorType.toUpperCase() === 'AUTODESK') ||
        (c.mcName && (c.mcName === 'Autodesk' || c.mcName.includes('Autodesk')))
      );
      const qbConnector = configData.connectors?.find((c) =>
        (c.mcConnectorType && c.mcConnectorType.toUpperCase() === 'QUICKBOOKS') ||
        (c.mcName && (c.mcName === 'QuickBooks' || c.mcName.includes('QuickBooks')))
      );

      // Same pattern as Budget to Item: use by-connector so backend resolves Autodesk token and container
      if (autodeskConnector?.mcId) {
        try {
          const contractsUrl = `${API_BASE_URL}/autodesk/contracts/by-connector?mcId=${autodeskConnector.mcId}&flowId=${flowIdForConnector}&tenantId=${tenantId}`;
          const contractsRes = await fetch(contractsUrl, {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
          if (contractsRes.ok) {
            const contractsJson = await contractsRes.json();
            setContractData(contractsJson);
          } else {
            const errBody = await contractsRes.json().catch(() => ({}));
            const msg = errBody?.message || errBody?.details || contractsRes.statusText;
            if (contractsRes.status === 401) {
              toast.warning('Autodesk token invalid or expired. Please reconnect the Autodesk connector.');
            } else if (contractsRes.status === 400 && (msg || '').toLowerCase().includes('container')) {
              toast.warning('No container assigned. Assign a container in Sync Flow settings to load contracts.');
            } else {
              toast.warning('Failed to load Autodesk contracts: ' + msg);
            }
          }
        } catch (e) {
          console.error(e);
          toast.warning('Error fetching contracts: ' + e.message);
        }
      } else {
        console.warn('ContractToPurchaseDatapoint: No Autodesk connector found');
      }

      if (qbConnector?.mcId) {
        try {
          const poRes = await fetch(
            `${API_BASE_URL}/quickbooks/purchase-orders/by-connector?mcId=${qbConnector.mcId}&flowId=${flowIdForConnector}&tenantId=${tenantId}`,
            { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } }
          );
          if (poRes.ok) {
            const poJson = await poRes.json();
            setPoData(poJson);
          } else {
            toast.warning('Failed to load QuickBooks purchase orders.');
          }
        } catch (e) {
          console.error(e);
          toast.warning('Error fetching purchase orders: ' + e.message);
        }
      }
    } catch (err) {
      console.error('ContractToPurchaseDatapoint:', err);
      setError('Failed to load data: ' + err.message);
      toast.error('Failed to load data point: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const contracts = contractData?.results ?? [];
  const purchaseOrders = poData?.PurchaseOrder ?? [];

  const mergedData = useMemo(() => {
    const merged = [];
    const matchedPoIds = new Set();

    // Match by Autodesk contract externalId === QuickBooks PO Id (no sync_track)
    contracts.forEach((contract) => {
      const sourceExternalId = String(contract.externalId ?? '').trim();
      const po = sourceExternalId
        ? purchaseOrders.find((p) => String(p.Id ?? p.id ?? '').trim() === sourceExternalId)
        : null;
      if (po) matchedPoIds.add(po.Id ?? po.id);

      merged.push({
        status: po ? 'matched' : 'source-only',
        source: contract,
        target: po || null,
      });
    });

    purchaseOrders.forEach((po) => {
      if (matchedPoIds.has(po.Id ?? po.id)) return;
      merged.push({
        status: 'target-only',
        source: null,
        target: po,
      });
    });

    return merged;
  }, [contracts, purchaseOrders]);

  const filteredData = useMemo(() => {
    let filtered = mergedData;
    if (statusFilter !== 'all') filtered = filtered.filter((i) => i.status === statusFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((i) => {
        const src = i.source
          ? `${i.source.name || ''} ${i.source.title || ''} ${i.source.id || ''} ${i.source.code || ''} ${i.source.externalId || ''} ${i.source.contractNumber || ''}`.toLowerCase()
          : '';
        const tgt = i.target
          ? `${i.target.DocNumber || ''} ${i.target.Id || ''} ${(i.target.VendorRef && (i.target.VendorRef.name || i.target.VendorRef.value)) || ''}`.toLowerCase()
          : '';
        return src.includes(q) || tgt.includes(q);
      });
    }
    return filtered;
  }, [mergedData, statusFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const getRowStyle = (status) => {
    switch (status) {
      case 'matched':
        return { backgroundColor: '#e8f5e9' };
      case 'source-only':
        return { backgroundColor: '#fff3e0' };
      case 'target-only':
        return { backgroundColor: '#e3f2fd' };
      default:
        return {};
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'matched':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />;
      case 'source-only':
        return <Warning sx={{ color: '#ff9800', fontSize: 20 }} />;
      case 'target-only':
        return <Info sx={{ color: '#2196f3', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'matched':
        return 'Synced';
      case 'source-only':
        return 'Not in Target';
      case 'target-only':
        return 'Target Only';
      default:
        return 'Unknown';
    }
  };

  const formatAmount = (val) => {
    if (val == null || val === undefined) return '—';
    const n = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(n)) return '—';
    return formatAppMoney(n);
  };

  const handleBack = () => navigate('/sync-flow');
  const handleRefresh = () => {
    setLoading(true);
    setContractData(null);
    setPoData(null);
    fetchDataPointConfig();
  };

  const renderEnhancedFilters = () => (
    <Collapse in={showFilters}>
      <Card sx={{ mb: 2, backgroundColor: '#f8f9fa' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search contracts or POs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Items</MenuItem>
                <MenuItem value="matched">Synced Only</MenuItem>
                <MenuItem value="source-only">Not Synced</MenuItem>
                <MenuItem value="target-only">Target Only</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows per page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Rows per page"
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>
    </Collapse>
  );

  const escapeCsvCell = (v) => {
    const s = String(v == null ? '' : v);
    if (/[,"\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const handleExportCsv = () => {
    if (!filteredData.length) {
      toast.info('No data to export');
      return;
    }
    const headers = ['Sync Status', 'Contract ID', 'Contract Code', 'Contract externalId', 'Contract Name', 'Contract Status', 'PO Id', 'PO DocNumber', 'PO Total', 'PO Vendor'];
    const getRow = (item) => [
      getStatusLabel(item.status),
      item.source?.id ?? item.source?.contractId ?? '',
      item.source?.code ?? '',
      item.source?.externalId ?? '',
      item.source?.name ?? item.source?.title ?? item.source?.contractNumber ?? '',
      item.source?.status ?? '',
      item.target?.Id ?? item.target?.id ?? '',
      item.target?.DocNumber ?? '',
      item.target?.TotalAmt ?? '',
      item.target?.VendorRef?.name ?? item.target?.VendorRef?.value ?? '',
    ];
    const csv = '\uFEFF' + [headers.map(escapeCsvCell).join(','), ...filteredData.map((i) => getRow(i).map(escapeCsvCell).join(','))].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `datapoint-contract-po-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filteredData.length} rows`);
  };

  if (loading && !config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back to Sync Flow
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back to Sync Flow
        </Button>
      </Box>

      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              Autodesk Contract ↔ QuickBooks Purchase Order ({filteredData.length} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {renderEnhancedFilters()}

          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
                {!containerId && ' • Assign a container in Sync Flow settings to load contracts'}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
                <Tooltip title="Download all as CSV">
                  <IconButton size="small" onClick={handleExportCsv} color="primary">
                    <FileDownload fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Contract ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Contract Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Contract externalId</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Contract Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Contract Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 50 }} align="center">
                    <SyncAlt />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>PO Id</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>PO DocNumber</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>PO Total</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>PO Vendor Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm || statusFilter !== 'all'
                          ? 'No items match your filters'
                          : 'No data available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        ...getRowStyle(item.status),
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <TableCell>
                        <Tooltip title={getStatusLabel(item.status)}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>{getStatusIcon(item.status)}</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: item.source ? 500 : undefined }}>
                          {item.source?.id ?? item.source?.contractId ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.source?.code ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.source?.externalId ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: item.source ? 500 : undefined }}>
                          {item.source?.name ?? item.source?.title ?? item.source?.contractNumber ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.source?.status ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <SyncAlt sx={{ color: item.status === 'matched' ? '#4caf50' : '#ccc' }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: item.target ? 500 : undefined }}>
                          {item.target?.Id ?? item.target?.id ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.target?.DocNumber ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatAmount(item.target?.TotalAmt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.target?.VendorRef?.name ?? item.target?.VendorRef?.value ?? '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ContractToPurchaseDatapoint;
